import React, { useState } from "react";
import { toast } from "sonner";
import { Building2, Calendar, Video, MapPin, ChevronDown, ChevronUp, CheckCircle2, Send, Loader2, FileText } from "lucide-react";
import { api } from "../../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Painel visto pela FAMÍLIA — mostra clínicas sugeridas anonimamente
 * (só código P00X + cidade/UF). Endereço, nome e contato ficam com o consultor.
 * A família pode ESCOLHER um plano → envia ao consultor pra seguir.
 */
export const SharedClinicsPanel = ({ shared, caseId, onSelected }) => {
  const [openIdx, setOpenIdx] = useState(0);
  const [selectingKey, setSelectingKey] = useState(null);
  const [notesByKey, setNotesByKey] = useState({});
  const alreadySelected = (shared || []).some((s) => s.is_selected);

  if (!shared || shared.length === 0) return null;

  const selectPlan = async (s, p) => {
    const key = `${s.clinic_id_internal}__${p.tier}`;
    const notes = notesByKey[key] || "";
    if (!window.confirm(`Confirmar escolha do plano '${p.tier}' da Clínica ${s.clinic_code}? O consultor será avisado imediatamente.`)) return;
    setSelectingKey(key);
    try {
      const { data } = await api.post(`/cases/${caseId}/select-plan`, {
        clinic_id_internal: s.clinic_id_internal,
        plan_tier: p.tier,
        notes: notes || null,
      });
      if (data?.auto_reserved) {
        toast.success("Plano confirmado! Reserva na clínica iniciada automaticamente.");
      } else {
        toast.success("Plano enviado! Consultor está a par.");
      }
      onSelected?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar escolha");
    } finally { setSelectingKey(null); }
  };

  const openReceipt = () => {
    // Download com cookie de sessão (raf_session httpOnly) — abre em nova aba,
    // o browser envia credenciais automaticamente para o backend.
    window.open(`${API_BASE}/api/cases/${caseId}/plan-receipt.pdf`, "_blank", "noopener");
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6" data-testid="family-shared-clinics">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-slate-500">Sugestões do consultor</div>
        <h2 className="font-display font-bold text-xl text-[#0B2B62] mt-1 flex items-center gap-2">
          <Building2 className="w-5 h-5" /> Unidades sugeridas
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Seu consultor RAF selecionou estas unidades. {!alreadySelected
            ? "Escolha o plano que preferir e clique em 'Escolher este plano' — seu consultor será avisado na hora."
            : "Sua escolha já foi enviada — o consultor está seguindo com a reserva."}
        </p>
        {alreadySelected && (
          <button onClick={openReceipt} data-testid="download-plan-receipt"
            className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-[#0B2B62] text-[#0B2B62] rounded-md text-xs font-semibold hover:bg-[#0B2B62] hover:text-white transition-colors">
            <FileText className="w-3.5 h-3.5" /> Baixar recibo (PDF)
          </button>
        )}
      </div>
      <div className="space-y-3">
        {shared.map((s, i) => {
          const isOpen = openIdx === i;
          return (
            <div key={s.id} className={`border rounded-lg overflow-hidden ${s.is_selected ? "border-emerald-400 ring-2 ring-emerald-200" : "border-slate-200"}`} data-testid={`shared-${i}`}>
              <button onClick={() => setOpenIdx(isOpen ? -1 : i)}
                className="w-full p-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3 min-w-0 text-left">
                  <div className={`w-10 h-10 rounded-md text-white flex items-center justify-center font-mono text-xs shrink-0 ${s.is_selected ? "bg-emerald-700" : "bg-[#0B2B62]"}`}>
                    {s.clinic_code}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-[#0B2B62] flex items-center gap-2">
                      Clínica {s.clinic_code}
                      {s.is_selected && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-bold uppercase tracking-widest inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Escolhida
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {s.clinic_city}, {s.clinic_state}
                      <span className="text-slate-300">·</span>
                      {(s.gallery?.length || 0)} foto(s) · {(s.plans?.length || 0)} plano(s)
                    </div>
                  </div>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
              </button>
              {isOpen && (
                <div className="p-4 border-t border-slate-100 space-y-4 bg-slate-50/40">
                  {s.description && (
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{s.description}</p>
                  )}
                  {(s.gallery || []).length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Galeria</div>
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-1.5">
                        {s.gallery.map((url, j) => (
                          <a key={url} href={url} target="_blank" rel="noreferrer" data-testid={`shared-${i}-photo-${j}`}>
                            <img src={url} alt="" className="aspect-square object-cover rounded-md hover:opacity-90 transition-opacity" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {(s.plans || []).length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Planos disponíveis</div>
                      <div className="space-y-2">
                        {s.plans.map((p, k) => {
                          const key = `${s.clinic_id_internal}__${p.tier}`;
                          const busy = selectingKey === key;
                          return (
                            <div key={key} className={`p-3 border rounded-md ${p.is_selected ? "bg-emerald-50 border-emerald-400" : "bg-white border-slate-200"}`} data-testid={`shared-${i}-plan-${k}`}>
                              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <div className="font-semibold text-sm text-[#0B2B62] flex items-center gap-1.5">
                                  {p.tier}
                                  {p.is_selected && <CheckCircle2 className="w-4 h-4 text-emerald-700" />}
                                </div>
                                {p.monthly_price != null && (
                                  <span className="text-xs font-mono text-slate-700">
                                    R$ {Number(p.monthly_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/mês
                                  </span>
                                )}
                              </div>
                              {p.description && <p className="text-xs text-slate-600 mt-1 whitespace-pre-line">{p.description}</p>}
                              {(p.includes || []).length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {p.includes.map((it) => (
                                    <span key={it} className="text-[10px] bg-slate-100 rounded-full px-2 py-0.5 text-slate-700">{it}</span>
                                  ))}
                                </div>
                              )}
                              {(p.room_photos || []).length > 0 && (
                                <div className="mt-2 grid grid-cols-4 gap-1">
                                  {p.room_photos.map((url) => (
                                    <img key={url} src={url} alt="" className="aspect-square object-cover rounded" />
                                  ))}
                                </div>
                              )}
                              {/* Ação: escolher plano */}
                              {!alreadySelected && s.clinic_id_internal && (
                                <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                                  <input type="text" placeholder="Observação para o consultor (opcional)"
                                    value={notesByKey[key] || ""}
                                    onChange={(e) => setNotesByKey({ ...notesByKey, [key]: e.target.value })}
                                    className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#0B2B62]"
                                    data-testid={`shared-${i}-plan-${k}-notes`}
                                  />
                                  <button onClick={() => selectPlan(s, p)} disabled={busy}
                                    data-testid={`shared-${i}-plan-${k}-select`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0B2B62] text-white rounded-md text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 w-full justify-center">
                                    {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando…</> : <><Send className="w-3.5 h-3.5" /> Escolher este plano</>}
                                  </button>
                                </div>
                              )}
                              {p.is_selected && (
                                <div className="mt-3 pt-3 border-t border-emerald-200 text-[11px] text-emerald-700 font-semibold flex items-center gap-1.5">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Este é o plano que você escolheu.
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400">
                    Compartilhado por {s.shared_by_name} · {new Date(s.shared_at).toLocaleString("pt-BR")}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

/** Painel de agendamentos — família só vê tipo + data + código anônimo. */
export const AppointmentsPanel = ({ appointments }) => {
  const list = (appointments || []).filter((a) => a.status !== "cancelado");
  if (list.length === 0) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6" data-testid="family-appointments">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-slate-500">Agendamentos</div>
        <h2 className="font-display font-bold text-xl text-[#0B2B62] mt-1 flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Próximos compromissos
        </h2>
      </div>
      <ul className="space-y-2">
        {list.map((a, i) => {
          const Icon = a.type === "videochamada" ? Video : MapPin;
          return (
            <li key={a.id} data-testid={`appointment-${i}`}
              className="p-3 border border-slate-200 rounded-lg flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-blue-50 text-[#0B2B62] flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-[#0B2B62]">
                  {a.type === "videochamada" ? "Videochamada" : "Visita presencial"} · Clínica {a.clinic_code}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {new Date(a.when).toLocaleString("pt-BR", { dateStyle: "full", timeStyle: "short" })}
                </div>
                {a.notes && <p className="text-xs text-slate-600 mt-1.5 whitespace-pre-line">{a.notes}</p>}
                <div className="text-[10px] text-slate-400 mt-1.5">
                  O consultor {a.created_by_name} enviará o endereço/link no seu chat, próximo à data.
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
